/**
 * FieldJob - App shell, routing, auth
 * Version: 2.0
 */
import React, { useState, useEffect, useRef, useCallback, createContext,
                useContext, lazy, Suspense } from "react";
import {
  OWNER_EMAIL, OWNER_PASSWORD, isOwner,
  V, FormError, NotifContext, NotifProvider,
  IDB, Icon, DeleteConfirmModal,
  DB, SUPABASE_URL, SUPABASE_ANON_KEY, supabase,
  formatZAR, getCurrencyOption,
  formatDate, today, addDays, daysLeft,
  CSS,
} from "./core.jsx";
import {
  ShareModal, LandingPage, AuthPage, ForgotPage, SetPasswordPage, ExpiredScreen, Sidebar,
  MobileKpiStrip, Dashboard, CalendarView,
  JobsModule, QuotesModule, EstimatesModule, InvoicesModule, ReceiptsModule,
  TemplatesModule, ClientsModule, WorkersModule, PortalsModule,
  ExpensesModule, InventoryModule, CashflowModule, ComplianceModule,
  ReportsModule, SettingsModule,
} from "./modules.jsx";

// ── ERROR BOUNDARY ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Module error:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
                      justifyContent:"center", padding:"60px 24px", textAlign:"center" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
               stroke="rgba(239,68,68,0.5)" strokeWidth="1.5" style={{marginBottom:16}}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>Something went wrong</div>
          <div style={{ color:"var(--muted)", fontSize:13, marginBottom:20 }}>
            This section encountered an error. Your data is safe.
          </div>
          <button className="btn btn-outline"
            onClick={() => this.setState({ hasError: false, error: null })}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── MODULE SPINNER ────────────────────────────────────────────────────────────
function ModuleSpinner() {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
                  padding:"80px 24px" }}>
      <style>{`
        @keyframes fjSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes fjPulse { 0%,100%{opacity:0.4;transform:scale(0.95)} 50%{opacity:1;transform:scale(1)} }
        .fj-spinner { width:32px; height:32px; border:3px solid rgba(200,151,42,0.2);
                      border-top-color:var(--gold,#c8972a); border-radius:50%;
                      animation:fjSpin 0.8s linear infinite; }
      `}</style>
      <div className="fj-spinner" />
    </div>
  );
}

// ── WRAP MODULE WITH ERROR BOUNDARY + SPINNER ─────────────────────────────────
function Wrap({ children }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ModuleSpinner />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

function useUpdatePrompt() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const checkForWaiting = (reg) => {
      if (reg.waiting) {
        setWaitingWorker(reg.waiting);
        setUpdateAvailable(true);
      }
    };

    navigator.serviceWorker.getRegistration().then(reg => {
      if (!reg) return;
      checkForWaiting(reg);
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            setWaitingWorker(newWorker);
            setUpdateAvailable(true);
          }
        });
      });
    });

    const onFocus = () => {
      navigator.serviceWorker.getRegistration().then(reg => {
        if (reg) { reg.update(); checkForWaiting(reg); }
      });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const applyUpdate = () => {
    if (!waitingWorker) { window.location.reload(); return; }
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      window.location.reload();
    });
  };

  return { updateAvailable, applyUpdate };
}

function useInstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    // Never show if running as installed PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true ||
      document.referrer.includes("android-app://");
    if (isStandalone) { localStorage.setItem("fj_pwa_dismissed","1"); return; }
    if (localStorage.getItem("fj_pwa_dismissed")) return;

    // Only show popup on first 2 logins
    const loginCount = parseInt(localStorage.getItem("fj_login_count") || "0");
    if (loginCount <= 2) setShowPopup(true);

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);
    if (ios && loginCount <= 2) { setShowIOSGuide(false); return; }

    if (window.__fjDeferredPrompt) { setPrompt(window.__fjDeferredPrompt); return; }
    // Listen directly — also pick up already-fired deferred event
    const handleInstall = (e) => {
      e.preventDefault();
      window.__fjDeferredPrompt = e;
      if (!window.matchMedia("(display-mode: standalone)").matches) {
        setPrompt(e);
        setShowPopup(true);
      }
    };
    window.addEventListener("beforeinstallprompt", handleInstall);
    window.__fjOnInstallReady = handleInstall;

    // If it fired before we mounted, grab it now
    if (window.__fjDeferredPrompt && !prompt) {
      setPrompt(window.__fjDeferredPrompt);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstall);
      window.__fjOnInstallReady = null;
    };
  }, []);

  const install = async () => {
    if (isIOS) { setShowIOSGuide(true); return; }
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") { setPrompt(null); localStorage.setItem("fj_pwa_dismissed","1"); }
    setShowPopup(false);
  };

  const dismissPopup = () => {
    setShowPopup(false);
    localStorage.setItem("fj_pwa_dismissed","1");
  };

  const dismissIOS = () => { setShowIOSGuide(false); };

  return { canInstall: !!(prompt || isIOS), showPopup: showPopup && !!(prompt || isIOS), showIOSGuide, install, dismissPopup, dismissIOS };
}

export default 


// ─── REPORTS MODULE ───────────────────────────────────────────────────────────
function App() {
  // Clear any old demo data from previous localStorage-based version
  if (typeof window !== "undefined") {
    // Version-based cache bust — bump APP_VERSION to force all users to re-login on new deploy
    const APP_VERSION = "3.2"; // bumped to clear stale sessions
    if (localStorage.getItem("fj_app_version") !== APP_VERSION) {
      // New version detected — clear all app data so no stale sessions load
      Object.keys(localStorage).forEach(k => { if (k.startsWith("fj_")) localStorage.removeItem(k); });
      localStorage.setItem("fj_app_version", APP_VERSION);
    }
  }

  const [page, setPage] = useState("loading");
  const [user, setUser] = useState(null);

  // Restore session from localStorage or IndexedDB on startup
  useEffect(() => {
    const restoreSession = async () => {
      // If ?logout=1 is in the URL — clear everything and force login
      // Check for password reset link (hash format or PKCE code param)
      if (typeof window !== "undefined") {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        if (hashParams.get("type") === "recovery" && hashParams.get("access_token")) {
          setPage("set-password");
          return;
        }
        const code = queryParams.get("code");
        if (code) {
          try {
            const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=pkce`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
              body: JSON.stringify({ auth_code: code, code_verifier: "" }),
            });
            const d = await res.json();
            if (d.access_token) {
              window.location.hash = `access_token=${d.access_token}&refresh_token=${d.refresh_token || ""}&type=recovery`;
            }
          } catch {}
          setPage("set-password");
          return;
        }
      }

      if (typeof window !== "undefined" && window.location.search.includes("logout=1")) {
        Object.keys(localStorage).forEach(k => { if (k.startsWith("fj_")) localStorage.removeItem(k); });
        localStorage.removeItem("sb_token");
        localStorage.removeItem("sb_refresh_token");
        localStorage.removeItem("fj_stay_signed_in");
        try { await IDB.remove("fj_session"); } catch {}
        window.history.replaceState(null, "", window.location.pathname);
        setPage("login");
        return;
      }

      // 1. Try localStorage first
      try {
        const stored = JSON.parse(localStorage.getItem("fj_user"));

        // Owner account — no JWT needed, verify against env credentials
        if (stored && stored.verified === true && stored.id === "owner_admin" && stored.email === OWNER_EMAIL) {
          setUser(stored);
          const b = await DB.get("business", "owner_admin");
          setBusiness(b || {});
          setPage("app");
          return;
        }

        // Regular user — verify JWT token is still valid with Supabase Auth
        if (stored && stored.verified === true && stored.auth_id) {
          const sbToken = localStorage.getItem("sb_token");
          if (sbToken) {
            const { data: userData, error: tokenErr } = await supabase.auth.getUser();
            if (!tokenErr && userData?.user) {
              // Token valid — refresh plan from DB in case owner upgraded them
              try {
                const freshUser = await DB.getUserByEmail(stored.email);
                if (freshUser && freshUser.plan !== stored.plan) {
                  const updated = { ...stored, plan: freshUser.plan, demo_expires: freshUser.demo_expires };
                  localStorage.setItem("fj_user", JSON.stringify(updated));
                  setUser(updated);
                } else {
                  setUser(stored);
                }
              } catch { setUser(stored); }
              const b = await DB.get("business", stored.auth_id);
              setBusiness(b || {});
              setPage("app");
              return;
            }
            // Token expired — try refreshing silently
            const { data: refreshData, error: refreshErr } = await supabase.auth.refreshSession();
            if (!refreshErr && refreshData?.access_token) {
              setUser(stored);
              const b = await DB.get("business", stored.auth_id);
              setBusiness(b || {});
              setPage("app");
              return;
            }
          }
          // No valid token — clear and go to login
          localStorage.removeItem("fj_user");
          localStorage.removeItem("sb_token");
          localStorage.removeItem("sb_refresh_token");
          setPage("login");
          return;
        }

        if (stored) { localStorage.removeItem("fj_user"); }
      } catch {}

      // 2. Try IndexedDB (PWA offline — "Stay signed in") — only if user opted in
      try {
        const staySignedIn = localStorage.getItem("fj_stay_signed_in");
        const idbSession = staySignedIn ? await IDB.get("fj_session") : null;
        if (idbSession && idbSession.verified === true) {
          if (idbSession.id === "owner_admin") {
            localStorage.setItem("fj_user", JSON.stringify(idbSession));
            setUser(idbSession);
            setPage("app");
            DB.get("business", "owner_admin").then(b => { if (b) setBusiness(b); }).catch(() => {});
            return;
          }
          if (idbSession.auth_id || idbSession.id?.startsWith("user_")) {
            localStorage.setItem("fj_user", JSON.stringify(idbSession));
            setUser(idbSession);
            setPage("app");
            DB.get("business", idbSession.auth_id || idbSession.id).then(b => { if (b) setBusiness(b); }).catch(() => {});
            return;
          }
        }
      } catch {}

      // 3. No valid session — go to login
      localStorage.removeItem("fj_user");
      setPage("login");
    };

    restoreSession();
  }, []);
  const [activeNav, setActiveNav] = useState("dashboard");
  const [business, setBusiness] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // ── PLUGIN REGISTRY ────────────────────────────────────────────────────────
  const PLUGINS = [
    {
      id:          "fieldsquare",
      name:        "FieldSquare",
      description: "Floor planning & estimating — draw rooms, calculate materials, labour and fuel. Desktop & tablet.",
      icon:        "tools",
      url:         "https://fieldsquare.getdiswayne.com",
      category:    "Estimating",
    },
  ];
  const { canInstall, showPopup, showIOSGuide, install, dismissPopup, dismissIOS } = useInstallPrompt();
  const { updateAvailable, applyUpdate } = useUpdatePrompt();

  // Suppress install prompt if already running as PWA
  if (typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone)) {
    localStorage.setItem("fj_pwa_dismissed", "1");
  }

  // Ensure mobile viewport prevents user-scalable zoom (restore if missing from index.html)
  useEffect(() => {
    const meta = document.querySelector("meta[name=viewport]");
    if (meta) {
      meta.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no");
    } else {
      const m = document.createElement("meta");
      m.name = "viewport";
      m.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
      document.head.appendChild(m);
    }
  }, []);

  const handleLogout = async () => {
    localStorage.removeItem("fj_user");
    localStorage.removeItem("sb_token");
    Object.keys(localStorage).forEach(k => { if (k.startsWith("fj_")) localStorage.removeItem(k); });
    await IDB.remove("fj_session");
    setUser(null);
    setPage("login");
    setActiveNav("dashboard");
  };

  // Expose logout globally so sidebar/settings can call it
  if (typeof window !== "undefined") window.__fjLogout = handleLogout;

  const handleAuth = async (u) => {
    setUser(u);
    const b = await DB.get("business", u.id);
    setBusiness(b || {});
    const count = parseInt(localStorage.getItem("fj_login_count") || "0");
    localStorage.setItem("fj_login_count", String(count + 1));
    setPage("app");
    // Pre-cache all data tables in the background so offline mode works next time
    // This runs silently after the user is already in the app
    if (navigator.onLine) {
      const tables = ["jobs","quotes","invoices","receipts","clients","workers","expenses","inventory","estimates","reports","compliance"];
      tables.forEach(t => {
        DB.getList(t, u.id).catch(() => {});
      });
    }
  };
  const handleNav = (p) => { setPage(p); }

  // Check if demo expired
  const isExpired = user?.plan === "demo" && user?.demo_expires && user.demo_expires < today();

  const pageTitles = { dashboard:"Dashboard", calendar:"Calendar & Scheduling", jobs:"Jobs", quotes:"Quotes", estimates:"Estimates", invoices:"Invoices", receipts:"Receipts", templates:"Templates", clients:"Clients", workers:"Workers", expenses:"Expense Tracking", inventory:"Stock & Inventory", cashflow:"Cashflow Intelligence", compliance:"Compliance & Certification", reports:"Reports", tools:"Plugins & Tools", settings:"Settings" };

  // If already installed as PWA, permanently suppress install prompt
  if (typeof window !== "undefined") {
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true) {
      localStorage.setItem("fj_pwa_dismissed", "1");
    }
  }

  // Show FJ splash screen while session is being restored from IDB
  // Offline banner state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline  = () => {
      setIsOffline(false);
      // When reconnecting, silently refresh all cached data
      if (user?.id) {
        const tables = ["jobs","quotes","invoices","receipts","clients","workers","expenses","inventory","estimates","reports","compliance"];
        tables.forEach(t => DB.getList(t, user.id).catch(() => {}));
        DB.get("business", user.id).then(b => { if (b) setBusiness(b); }).catch(() => {});
      }
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online",  goOnline);
    return () => { window.removeEventListener("offline", goOffline); window.removeEventListener("online", goOnline); };
  }, [user]);

  if (page === "loading") return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", flexDirection:"column", gap:0, position:"relative", overflow:"hidden", background:"#ffffff" }}>
      <style>{`
        @keyframes fjPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(0.97)} }
        @keyframes dotBounce { 0%,80%,100%{transform:translateY(0);opacity:0.25} 40%{transform:translateY(-8px);opacity:1} }
        .fj-logo { animation: fjPulse 2.4s ease-in-out infinite; }
        .fj-dot { width:7px;height:7px;border-radius:50%;background:#c8972a;display:inline-block;margin:0 4px;animation:dotBounce 1.2s ease-in-out infinite; }
        .fj-dot:nth-child(2){animation-delay:0.18s}
        .fj-dot:nth-child(3){animation-delay:0.36s}
        @keyframes wflow1 { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes wflow2 { 0%{transform:translateX(-50%)} 100%{transform:translateX(0)} }
        .fj-wt1 { animation: wflow1 9s linear infinite; }
        .fj-wt2 { animation: wflow2 12s linear infinite; }
        @media (max-width: 768px) {
          .fj-loading-bg { background: #0f1923 !important; }
          .fj-loading-title { color: white !important; }
          .fj-loading-sub { color: rgba(255,255,255,0.3) !important; }
          .fj-loading-mark { background: rgba(200,151,42,0.12) !important; border-color: rgba(200,151,42,0.3) !important; }
          .fj-loading-fj { color: var(--gold-light) !important; }
          .fj-waves { display: none !important; }
        }
      `}</style>

      {/* Wave bundles — desktop only */}
      <div className="fj-waves" style={{ position:"absolute", top:0, left:0, right:0, bottom:0, overflow:"hidden", pointerEvents:"none" }}>

        {/* Bundle 1 — main wave crossing upper-mid */}
        <div className="fj-wt1" style={{ position:"absolute", top:"42%", left:0, width:"200%", height:160, marginTop:-80 }}>
          <svg viewBox="0 0 1440 160" preserveAspectRatio="none" width="100%" height="100%" style={{ display:"block" }}>
            <g fill="none" strokeLinecap="round">
              <path d="M0,20 C240,95 480,-15 720,20 C960,55 1200,95 1440,20" stroke="rgba(200,150,30,0.07)" strokeWidth="1"/>
              <path d="M0,26 C240,98 480,-8 720,26 C960,60 1200,98 1440,26" stroke="rgba(200,150,30,0.10)" strokeWidth="1"/>
              <path d="M0,32 C240,100 480,-2 720,32 C960,64 1200,100 1440,32" stroke="rgba(200,150,30,0.15)" strokeWidth="1"/>
              <path d="M0,38 C240,102 480,4 720,38 C960,68 1200,102 1440,38" stroke="rgba(200,150,30,0.20)" strokeWidth="1.2"/>
              <path d="M0,44 C240,104 480,10 720,44 C960,72 1200,104 1440,44" stroke="rgba(200,150,30,0.26)" strokeWidth="1.3"/>
              <path d="M0,50 C240,106 480,16 720,50 C960,76 1200,106 1440,50" stroke="rgba(200,148,25,0.38)" strokeWidth="1.5"/>
              <path d="M0,56 C240,108 480,22 720,56 C960,80 1200,108 1440,56" stroke="rgba(210,155,28,0.44)" strokeWidth="1.6"/>
              <path d="M0,62 C240,110 480,28 720,62 C960,84 1200,110 1440,62" stroke="rgba(215,160,30,0.48)" strokeWidth="1.7"/>
              <path d="M0,68 C240,112 480,34 720,68 C960,88 1200,112 1440,68" stroke="rgba(210,155,28,0.44)" strokeWidth="1.6"/>
              <path d="M0,74 C240,114 480,40 720,74 C960,92 1200,114 1440,74" stroke="rgba(200,148,25,0.38)" strokeWidth="1.5"/>
              <path d="M0,80 C240,116 480,46 720,80 C960,96 1200,116 1440,80" stroke="rgba(200,150,30,0.26)" strokeWidth="1.3"/>
              <path d="M0,86 C240,118 480,52 720,86 C960,100 1200,118 1440,86" stroke="rgba(200,150,30,0.20)" strokeWidth="1.2"/>
              <path d="M0,92 C240,120 480,58 720,92 C960,104 1200,120 1440,92" stroke="rgba(200,150,30,0.15)" strokeWidth="1"/>
              <path d="M0,98 C240,122 480,64 720,98 C960,108 1200,122 1440,98" stroke="rgba(200,150,30,0.10)" strokeWidth="1"/>
              <path d="M0,104 C240,124 480,70 720,104 C960,112 1200,124 1440,104" stroke="rgba(200,150,30,0.07)" strokeWidth="1"/>
            </g>
          </svg>
        </div>

        {/* Bundle 2 — counter direction, lower */}
        <div className="fj-wt2" style={{ position:"absolute", top:"60%", left:0, width:"200%", height:130, marginTop:-65 }}>
          <svg viewBox="0 0 1440 130" preserveAspectRatio="none" width="100%" height="100%" style={{ display:"block" }}>
            <g fill="none" strokeLinecap="round">
              <path d="M0,18 C200,80 400,8 600,18 C800,28 1000,80 1200,18 C1320,0 1400,50 1440,18" stroke="rgba(200,150,30,0.07)" strokeWidth="1"/>
              <path d="M0,24 C200,82 400,14 600,24 C800,34 1000,82 1200,24 C1320,6 1400,52 1440,24" stroke="rgba(200,150,30,0.11)" strokeWidth="1"/>
              <path d="M0,30 C200,84 400,20 600,30 C800,40 1000,84 1200,30 C1320,12 1400,54 1440,30" stroke="rgba(200,150,30,0.17)" strokeWidth="1.2"/>
              <path d="M0,36 C200,86 400,26 600,36 C800,46 1000,86 1200,36 C1320,18 1400,56 1440,36" stroke="rgba(200,148,25,0.28)" strokeWidth="1.4"/>
              <path d="M0,42 C200,88 400,32 600,42 C800,52 1000,88 1200,42 C1320,24 1400,58 1440,42" stroke="rgba(210,155,28,0.36)" strokeWidth="1.5"/>
              <path d="M0,48 C200,90 400,38 600,48 C800,58 1000,90 1200,48 C1320,30 1400,60 1440,48" stroke="rgba(215,160,30,0.40)" strokeWidth="1.6"/>
              <path d="M0,54 C200,92 400,44 600,54 C800,64 1000,92 1200,54 C1320,36 1400,62 1440,54" stroke="rgba(210,155,28,0.36)" strokeWidth="1.5"/>
              <path d="M0,60 C200,94 400,50 600,60 C800,70 1000,94 1200,60 C1320,42 1400,64 1440,60" stroke="rgba(200,148,25,0.28)" strokeWidth="1.4"/>
              <path d="M0,66 C200,96 400,56 600,66 C800,76 1000,96 1200,66 C1320,48 1400,66 1440,66" stroke="rgba(200,150,30,0.17)" strokeWidth="1.2"/>
              <path d="M0,72 C200,98 400,62 600,72 C800,82 1000,98 1200,72 C1320,54 1400,68 1440,72" stroke="rgba(200,150,30,0.11)" strokeWidth="1"/>
              <path d="M0,78 C200,100 400,68 600,78 C800,88 1000,100 1200,78 C1320,60 1400,70 1440,78" stroke="rgba(200,150,30,0.07)" strokeWidth="1"/>
            </g>
          </svg>
        </div>

      </div>

      {/* Logo mark */}
      <div className="fj-logo" style={{ marginBottom:28, position:"relative", zIndex:2 }}>
        <div className="fj-loading-mark" style={{ width:80, height:80, background:"#fff", border:"1.5px solid #c8a84b", borderRadius:22, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 24px rgba(200,168,75,0.18)" }}>
          <span className="fj-loading-fj" style={{ fontFamily:"var(--font-display)", fontSize:32, fontWeight:700, color:"#111", letterSpacing:0 }}>FJ</span>
        </div>
      </div>
      <div className="fj-loading-title" style={{ fontSize:18, fontWeight:700, color:"#111", marginBottom:6, letterSpacing:0.3, position:"relative", zIndex:2 }}>FieldJob</div>
      <div className="fj-loading-sub" style={{ fontSize:11, color:"#bbb", letterSpacing:2, textTransform:"uppercase", marginBottom:28, position:"relative", zIndex:2 }}>Contractor OS</div>
      <div style={{ display:"flex", alignItems:"center", position:"relative", zIndex:2 }}>
        <span className="fj-dot"></span>
        <span className="fj-dot"></span>
        <span className="fj-dot"></span>
      </div>
    </div>
  );

  // Inject styles
  if (typeof document !== "undefined" && !document.getElementById("fj-styles")) {
    const style = document.createElement("style");
    style.id = "fj-styles";
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // Routing
  if (page === "set-password") return <SetPasswordPage onNav={handleNav} onAuth={handleAuth} />;
  if (page === "landing") return <AuthPage mode="login" onNav={handleNav} onAuth={handleAuth} />;
  if (page === "login") return <AuthPage mode="login" onNav={handleNav} onAuth={handleAuth} />;
  if (page === "signup") return <AuthPage mode="signup" onNav={handleNav} onAuth={handleAuth} />;
  if (page === "forgot") return <ForgotPage onNav={handleNav} />;
  if (!user) return <AuthPage mode="login" onNav={handleNav} onAuth={handleAuth} />;

  // App
  const demoDaysLeft = user.plan === "demo" ? daysLeft(user.demo_expires) : 0;

  const todayLabel = (() => { const d = new Date(); return d.toLocaleDateString('en-ZA',{weekday:'long',day:'numeric',month:'long'}); })();
  const avatarInitial = (business?.name || user?.email || 'F')[0].toUpperCase();

  const bnSvg = {
    dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>,
    invoices: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>,
    jobs: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/></svg>,
    plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    more: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>,
  };

  const moreNav = ["calendar","invoices","quotes","estimates","reports","receipts","clients","workers","portals","templates","expenses","inventory","cashflow","compliance","tools","settings"];
  const moreLabels = { calendar:"Calendar", invoices:"Invoices", quotes:"Quotes", estimates:"Estimates", reports:"Reports", receipts:"Receipts", clients:"Clients", workers:"Workers", portals:"Portals", templates:"Templates", expenses:"Expenses", inventory:"Inventory", cashflow:"Cashflow", compliance:"Compliance", tools:"Tools", settings:"Settings" };

  return (
    <NotifProvider userId={user?.auth_id || user?.id} isOwner={true}>
    <div className="app">
      <Sidebar active={activeNav} onNav={setActiveNav} user={user} business={business} mobileOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      <main className="main">

        {/* ── UPDATE BANNER ── */}
        {updateAvailable && (
          <div style={{
            background:"linear-gradient(135deg, var(--ink), #1a2535)",
            borderBottom:"1px solid rgba(200,151,42,0.4)",
            padding:"10px 20px",
            display:"flex", alignItems:"center", justifyContent:"space-between",
            gap:12, zIndex:100, position:"relative",
          }}>
            <div style={{display:"flex", alignItems:"center", gap:10}}>
              <div style={{
                width:28, height:28, borderRadius:8, flexShrink:0,
                background:"linear-gradient(145deg,rgba(200,151,42,0.3),rgba(200,151,42,0.1))",
                border:"1px solid rgba(200,151,42,0.4)",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </div>
              <div>
                <div style={{fontWeight:700, fontSize:12.5, color:"white"}}>New version available</div>
                <div style={{fontSize:11, color:"rgba(255,255,255,0.45)"}}>Tap to update FieldJob now</div>
              </div>
            </div>
            <button
              onClick={applyUpdate}
              style={{
                background:"var(--gold)", color:"var(--ink)", border:"none",
                borderRadius:8, padding:"7px 16px", fontWeight:700, fontSize:12.5,
                cursor:"pointer", flexShrink:0, whiteSpace:"nowrap",
              }}>
              Update Now
            </button>
          </div>
        )}

        {/* ── MOBILE HEADER (inside main so it's in the vertical flow) ── */}
        <div className="mobile-header">
          <div className="mobile-header-top">
            <div>
              <div className="mobile-greeting">Good day 👋</div>
              <div className="mobile-biz-name">{business?.name || "FieldJob"}</div>
              <div className="mobile-date">{todayLabel}</div>
            </div>
            <div className="mobile-avatar">{avatarInitial}</div>
          </div>
          <div className="kpi-strip">
            <MobileKpiStrip userId={user.id} onNav={setActiveNav} />
          </div>
        </div>

        {/* ── QUICK ACTIONS (inside main, below mobile header) ── */}
        <div className="quick-actions-row">
          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.8px",color:"#94a3b8",marginBottom:10}}>Quick Actions</div>
          <div className="qa-row-inner">
            {[
              { label:"New Job", icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="12" y1="9" x2="12" y2="15"/><line x1="9" y1="12" x2="15" y2="12"/></svg>, nav:"jobs" },
              { label:"Quote", icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>, nav:"quotes" },
              { label:"Invoice", icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="12" y1="1" x2="12" y2="6"/><line x1="9" y1="13" x2="13" y2="13"/></svg>, nav:"invoices" },
              { label:"Expense", icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>, nav:"expenses" },
              { label:"Estimate", icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="7" y1="3" x2="7" y2="9"/><line x1="7" y1="13" x2="10" y2="13"/><line x1="7" y1="17" x2="10" y2="17"/><line x1="14" y1="13" x2="17" y2="13"/><line x1="14" y1="17" x2="17" y2="17"/></svg>, nav:"estimates" },
            ].map(qa => (
              <div key={qa.label} className="qa-btn" onClick={() => setActiveNav(qa.nav)}>
                <div className="qa-tile">{qa.icon}</div>
                <div className="qa-btn-label">{qa.label}</div>
              </div>
            ))}
          </div>
        </div>
        {isOffline && (
          <div style={{ background:"#ef4444", color:"white", textAlign:"center", padding:"7px 16px", fontSize:12, fontWeight:700, letterSpacing:0.3, zIndex:9999 }}>
            📵 You are offline — showing saved data. Changes will sync when you reconnect.
          </div>
        )}
        {user.plan === "demo" && !isExpired && (
          <div className="demo-banner" style={{ background: demoDaysLeft <= 2 ? "rgba(220,38,38,0.12)" : demoDaysLeft <= 4 ? "rgba(245,158,11,0.12)" : "rgba(200,151,42,0.1)", borderBottom: `1px solid ${demoDaysLeft <= 2 ? "rgba(220,38,38,0.25)" : demoDaysLeft <= 4 ? "rgba(245,158,11,0.25)" : "rgba(200,151,42,0.2)"}` }}>
            <span style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:28, height:28, borderRadius:"50%", background: demoDaysLeft <= 2 ? "#dc2626" : demoDaysLeft <= 4 ? "#f59e0b" : "var(--gold)", color:"white", fontWeight:800, fontSize:13, flexShrink:0 }}>{demoDaysLeft}</span>
              <span><strong>{demoDaysLeft} day{demoDaysLeft!==1?"s":""}</strong> left on your free trial</span>
            </span>
            <button className="btn btn-gold btn-sm" onClick={() => setActiveNav("settings")}>Upgrade to Pro — R314/mo</button>
          </div>
        )}
        {showPopup && (
          <div style={{ background:"var(--gold)", color:"#1a1a1a", padding:"10px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:13, fontWeight:500 }}>
            <span>📲 Install FieldJob for the best experience — works offline too!</span>
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-sm" style={{ background:"#1a1a1a", color:"white", border:"none" }} onClick={install}>Install Now</button>
              <button className="btn btn-sm btn-outline" style={{ borderColor:"#1a1a1a" }} onClick={dismissPopup}>Not Now</button>
            </div>
          </div>
        )}
        <header className="topbar">
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <button className="btn btn-icon btn-outline" style={{ display:"none" }} onClick={() => setSidebarOpen(true)} id="mobile-menu"><Icon name="menu" /></button>
            <h2>{pageTitles[activeNav] || "FieldJob"}</h2>
          </div>
          <div className="topbar-actions">
            {business.name && <span className="text-muted text-sm" style={{marginRight:4}}>{business.name}</span>}
            {business.currency && (
              <button
                onClick={() => setActiveNav("settings")}
                title="Currency & Tax settings"
                style={{ background:"var(--gold-dim)", border:"1px solid var(--gold-b,rgba(200,151,42,0.25))", borderRadius:6, padding:"3px 8px", fontSize:11, fontWeight:700, color:"var(--gold)", cursor:"pointer", display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
                {getCurrencyOption(business.currency)?.flag} {business.currency}
              </button>
            )}
            {canInstall && (
              <button className="btn btn-outline btn-sm" onClick={install} style={{display:"flex",alignItems:"center",gap:5,fontSize:12}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Install App
              </button>
            )}
            {showIOSGuide && (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:999,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:16}} onClick={dismissIOS}>
                <div style={{background:"white",borderRadius:14,padding:24,maxWidth:360,width:"100%",marginBottom:20}} onClick={e=>e.stopPropagation()}>
                  <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>Install FieldJob on iOS</div>
                  <div style={{fontSize:13,color:"#64748b",lineHeight:1.7}}>Tap the <strong style={{color:"var(--gold)"}}>Share</strong> button in Safari, then tap <strong style={{color:"var(--gold)"}}>Add to Home Screen</strong>.</div>
                  <button className="btn btn-outline btn-sm" style={{marginTop:16}} onClick={dismissIOS}>Got it</button>
                </div>
              </div>
            )}
          </div>
        </header>

        {isExpired && <ExpiredScreen onUpgrade={() => setActiveNav("settings")} onLogout={handleLogout} />}

        <div className="page">
          {activeNav === "dashboard" && <Wrap><Dashboard userId={user.id} setActiveNav={setActiveNav} /></Wrap>}
          {activeNav === "calendar" && <Wrap><CalendarView userId={user.id} onNewJob={() => setActiveNav("jobs")} /></Wrap>}
          {activeNav === "jobs" && <Wrap><JobsModule userId={user.id} /></Wrap>}
          {activeNav === "quotes" && <Wrap><QuotesModule userId={user.id} /></Wrap>}
          {activeNav === "estimates" && <Wrap><EstimatesModule userId={user.id} /></Wrap>}
          {activeNav === "reports"   && <Wrap><ReportsModule userId={user.id} /></Wrap>}
          {activeNav === "invoices" && <Wrap><InvoicesModule userId={user.id} /></Wrap>}
          {activeNav === "receipts" && <Wrap><ReceiptsModule userId={user.id} /></Wrap>}
          {activeNav === "templates" && <Wrap><TemplatesModule userId={user.id} /></Wrap>}
          {activeNav === "clients" && <Wrap><ClientsModule userId={user.id} /></Wrap>}
          {activeNav === "workers" && <Wrap><WorkersModule userId={user.id} /></Wrap>}
          {activeNav === "portals" && <Wrap><PortalsModule userId={user.id} /></Wrap>}
          {activeNav === "expenses" && <Wrap><ExpensesModule userId={user.id} /></Wrap>}
          {activeNav === "inventory" && <Wrap><InventoryModule userId={user.id} /></Wrap>}
          {activeNav === "cashflow" && <Wrap><CashflowModule userId={user.id} /></Wrap>}
          {activeNav === "compliance" && <Wrap><ComplianceModule userId={user.id} /></Wrap>}
          {activeNav === "settings" && <Wrap><SettingsModule userId={user.id} onBusinessUpdate={setBusiness} /></Wrap>}
          {activeNav === "tools" && (
            <div style={{padding:"24px 20px",maxWidth:700}}>
              <p style={{color:"var(--muted)",fontSize:13,marginBottom:24}}>Enable or disable external tools for your workspace. Enabled tools can be accessed via the Open button.</p>
              {PLUGINS.map(plugin => {
                const isEnabled = !!(business?.plugins || {})[plugin.id];
                const togglePlugin = async () => {
                  const current = business?.plugins || {};
                  const updated = { ...current, [plugin.id]: !isEnabled };
                  const newBusiness = { ...business, plugins: updated };
                  setBusiness(newBusiness);
                  await DB.upsert("business", { ...newBusiness, user_id: user.id });
                };
                return (
                  <div key={plugin.id} style={{background:"var(--card)",border:`1px solid ${isEnabled ? "rgba(200,151,42,0.35)" : "var(--border)"}`,borderRadius:14,padding:"18px 20px",marginBottom:14,display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
                    <div style={{display:"flex",alignItems:"flex-start",gap:14,minWidth:0}}>
                      <div style={{width:44,height:44,borderRadius:12,background:isEnabled?"linear-gradient(135deg,rgba(200,151,42,0.2),rgba(200,151,42,0.05))":"rgba(255,255,255,0.03)",border:`1px solid ${isEnabled?"rgba(200,151,42,0.3)":"rgba(255,255,255,0.08)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <Icon name="tools" size={20} style={{color: isEnabled ? "var(--gold-light)" : "rgba(255,255,255,0.3)"}} />
                      </div>
                      <div style={{minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                          <div style={{fontWeight:700,fontSize:14}}>{plugin.name}</div>
                          {isEnabled && <span style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:20,background:"rgba(16,185,129,0.15)",color:"#10b981",border:"1px solid rgba(16,185,129,0.3)"}}>ENABLED</span>}
                        </div>
                        <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.5,marginBottom:8}}>{plugin.description}</div>
                        <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,background:"rgba(200,151,42,0.12)",color:"var(--gold-light)",border:"1px solid rgba(200,151,42,0.2)"}}>{plugin.category}</span>
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8,flexShrink:0}}>
                      <button onClick={togglePlugin} style={{padding:"6px 14px",borderRadius:8,border:"none",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",background:isEnabled?"rgba(239,68,68,0.12)":"rgba(200,151,42,0.15)",color:isEnabled?"#ef4444":"var(--gold-light)"}}>
                        {isEnabled ? "Disable" : "Enable"}
                      </button>
                      {isEnabled && (
                        <button className="btn btn-primary btn-sm" onClick={() => window.open(`${plugin.url}?uid=${user.id}`, "_blank", "noopener")}>
                          <Icon name="external" size={12} /> Open
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* ── BOTTOM NAV ── */}
      <nav className="bottom-nav">
        <div className="bottom-nav-inner" style={{width:"100%"}}>
          {[
            { id:"dashboard", label:"Home", svg: bnSvg.dashboard },
            { id:"jobs",      label:"Jobs",     svg: bnSvg.jobs },
            { id:"__plus",    label:"New Job",  svg: bnSvg.plus },
            { id:"invoices",  label:"Invoices", svg: bnSvg.invoices },
            { id:"__more",    label:"More",     svg: bnSvg.more },
          ].map(item => (
            <div key={item.id}
              className={"bn-item" + (activeNav === item.id ? " active" : "")}
              onClick={() => {
                if (item.id === "__plus") { setActiveNav("jobs"); }
                else if (item.id === "__more") { setMoreOpen(true); }
                else setActiveNav(item.id);
              }}>
              <div className="bn-tile">{item.svg}</div>
              {item.label}
            </div>
          ))}
        </div>
      </nav>

      {/* ── MORE DRAWER ── */}
      {moreOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300}} onClick={() => setMoreOpen(false)}>
          <div style={{position:"absolute",bottom:0,left:0,right:0,background:"var(--ink)",borderRadius:"20px 20px 0 0",padding:"20px 20px 0",maxHeight:"72vh",overflowY:"auto"}} onClick={e => e.stopPropagation()}>
            <div style={{width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 16px"}}></div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"14px 8px",paddingBottom:100}}>
              {moreNav.map(id => (
                <div key={id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:7,cursor:"pointer"}}
                  onClick={() => { setActiveNav(id); setMoreOpen(false); }}>
                  <div style={{
                    width:52,height:52,borderRadius:15,display:"flex",alignItems:"center",justifyContent:"center",
                    background: activeNav===id ? "linear-gradient(145deg,rgba(200,151,42,0.3),rgba(200,151,42,0.1))" : "rgba(255,255,255,0.06)",
                    border: activeNav===id ? "1px solid rgba(200,151,42,0.5)" : "1px solid rgba(255,255,255,0.08)",
                    boxShadow: activeNav===id ? "0 0 12px rgba(200,151,42,0.2)" : "none",
                    position:"relative",overflow:"hidden",
                  }}>
                    <div style={{position:"absolute",top:0,left:0,right:0,height:"50%",background:"linear-gradient(rgba(255,255,255,0.05),transparent)",borderRadius:"15px 15px 0 0"}}></div>
                    <Icon name={
                      id==="calendar"?"calendar":id==="invoices"?"invoice":id==="quotes"?"quote":id==="estimates"?"estimate":id==="receipts"?"receipt":
                      id==="clients"?"clients":id==="workers"?"workers":id==="portals"?"portal":id==="templates"?"pen":id==="expenses"?"expenses":id==="reports"?"report":
                      id==="inventory"?"inventory":id==="cashflow"?"cashflow":id==="compliance"?"compliance":id==="tools"?"tools":"settings"
                    } size={20} style={{color: activeNav===id ? "var(--gold-light)" : "rgba(255,255,255,0.5)", position:"relative",zIndex:1}} />
                  </div>
                  <span style={{fontSize:10,fontWeight:600,color: activeNav===id ? "var(--gold-light)" : "rgba(255,255,255,0.45)",textAlign:"center"}}>{moreLabels[id]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
    </NotifProvider>
  );
}
